import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { venturesData } from '../data/mock';
import { ArrowUpRight, ArrowRight, Zap, Clock, Wrench, ExternalLink } from 'lucide-react';
import { BrandedProductName } from './BrandedProductName';

gsap.registerPlugin(ScrollTrigger);

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

const VentureCard = ({ venture, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });
  const isMobile = useIsMobile();

  const isLive = venture.status === 'live';
  const isComingSoon = venture.status === 'coming_soon';

  useEffect(() => {
    if (isMobile) {
      if (cardRef.current) cardRef.current.style.opacity = '1';
      return;
    }
    if (isInView) {
      gsap.fromTo(
        cardRef.current,
        { y: 60, opacity: 0, scale: 0.97 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7, ease: 'power3.out', delay: index * 0.12 }
      );
    }
  }, [isInView, index, isMobile]);

  // Determine card styling based on venture
  const isFeatured = venture.featured;
  const bgClass = isFeatured
    ? 'bg-[#D3FF62] text-[#004534]'
    : 'bg-[#141414] text-white';
  const taglineColor = isFeatured ? 'text-[#004534]/60' : `text-[${venture.accentColor}]`;
  const descColor = isFeatured ? 'text-[#004534]/80' : 'text-white/60';
  const featureColor = isFeatured ? 'text-[#004534]/70' : 'text-white/70';
  const borderClass = isFeatured ? 'border-[#D3FF62]/30' : 'border-white/10 hover:border-white/20';

  const LinkWrapper = ({ children, className }) => {
    if (!isLive) return <div className={className}>{children}</div>;
    if (venture.linkType === 'external') {
      return <a href={venture.link} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>;
    }
    return <Link to={venture.link} className={className}>{children}</Link>;
  };

  return (
    <motion.div
      ref={cardRef}
      className={`relative p-8 rounded-3xl overflow-hidden card-hover ${bgClass} border ${borderClass} transition-all duration-300 flex flex-col`}
      style={{ opacity: isMobile ? 1 : 0 }}
      whileHover={isMobile ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
      data-testid={`venture-card-${venture.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      {/* Status Badge — flows in document on mobile, absolute on desktop */}
      <div className="md:absolute md:top-6 md:right-6 mb-4 md:mb-0">
        {isLive && (
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${isFeatured ? 'bg-[#004534]' : 'bg-white/10'}`}>
            <Zap size={14} className={isFeatured ? 'text-[#D3FF62]' : ''} style={{ color: isFeatured ? undefined : venture.accentColor }} />
            <span className={`text-xs font-bold ${isFeatured ? 'text-[#D3FF62]' : ''}`} style={{ color: isFeatured ? undefined : venture.accentColor }}>
              {venture.badgeLabel || (venture.name === 'THE DOSSIER' ? 'FOUNDING' : 'AVAILABLE NOW')}
            </span>
          </div>
        )}
        {isComingSoon && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
            <Clock size={14} className="text-[#D3FF62]" />
            <span className="text-xs font-semibold text-white">COMING SOON</span>
          </div>
        )}
      </div>

      {/* Tagline */}
      <p className={`text-sm font-bold uppercase tracking-widest mb-3 md:pr-36 ${isFeatured ? 'text-[#004534]/60' : ''}`}
        style={{ color: isFeatured ? undefined : venture.accentColor }}>
        {venture.tagline}
      </p>

      {/* Name */}
      <h3 className="heading-1 mb-1">
        <BrandedProductName product={venture.name} />
      </h3>

      {/* Subtitle if present */}
      {venture.subtitle && (
        <p className={`text-sm font-medium mb-3 ${isFeatured ? 'text-[#004534]/50' : 'text-white/40'}`}>
          {venture.subtitle}
        </p>
      )}

      {/* Description */}
      <p className={`body-medium mb-6 flex-grow ${descColor}`}>
        {venture.description}
      </p>

      {/* Features */}
      <div className="mb-8">
        {venture.features.map((feature, i) => (
          <div key={i} className={`flex items-center gap-3 py-2 ${i < venture.features.length - 1 ? `border-b ${isFeatured ? 'border-[#004534]/10' : 'border-white/5'}` : ''}`}>
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: isFeatured ? 'rgba(0,69,52,0.15)' : `${venture.accentColor}15` }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isFeatured ? '#004534' : venture.accentColor }} />
            </div>
            <span className={`text-sm ${featureColor}`}>{feature}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      {isLive ? (
        <LinkWrapper className={`inline-flex items-center gap-2 font-semibold group ${isFeatured ? 'text-[#004534]' : 'text-white'}`}>
          {venture.linkType === 'external' ? (
            <>
              Visit {venture.displayName || venture.name.charAt(0) + venture.name.slice(1).toLowerCase()}
              <ExternalLink size={18} className="transition-transform group-hover:translate-x-1" />
            </>
          ) : (
            <>
              Explore <BrandedProductName product={venture.name} />
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </>
          )}
        </LinkWrapper>
      ) : (
        <div className="inline-flex items-center gap-2 text-white/30 font-semibold cursor-default">
          <Clock size={18} />
          Coming Soon
        </div>
      )}
    </motion.div>
  );
};

// Custom Solutions Card
const CustomSolutionsCard = ({ index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      if (cardRef.current) cardRef.current.style.opacity = '1';
      return;
    }
    if (isInView) {
      gsap.fromTo(
        cardRef.current,
        { y: 60, opacity: 0, scale: 0.97 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7, ease: 'power3.out', delay: index * 0.12 }
      );
    }
  }, [isInView, index, isMobile]);

  const handleClick = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    window.dispatchEvent(new CustomEvent('preselectInquiry', { detail: 'custom' }));
  };

  return (
    <motion.div
      ref={cardRef}
      className="relative p-8 rounded-3xl overflow-hidden card-hover bg-[#141414] text-white border border-dashed border-white/20 hover:border-[#D3FF62]/30 transition-all duration-300 flex flex-col"
      style={{ opacity: isMobile ? 1 : 0 }}
      whileHover={isMobile ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
      data-testid="custom-solutions-card"
    >
      <div className="relative z-10 flex flex-col h-full">
        <p className="text-sm font-bold uppercase tracking-widest mb-3 text-[#D3FF62]">
          TAILORED TO YOUR NEEDS
        </p>

        <h3 className="heading-1 mb-4">
          CUSTOM SOLUTIONS
        </h3>

        <p className="body-medium text-white/60 mb-8 flex-grow">
          Don't see what you need? We partner with teams, agencies, schools, and athletic programs to build
          tailored software solutions for your unique workflows and challenges.
        </p>

        <a
          href="#contact"
          onClick={handleClick}
          className="inline-flex items-center gap-2 font-semibold text-white group hover:text-[#D3FF62] transition-colors"
          data-testid="custom-solutions-cta"
        >
          Let's Talk
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </motion.div>
  );
};

const VenturesSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      if (titleRef.current) {
        Array.from(titleRef.current.children).forEach(child => {
          child.style.opacity = '1';
          child.style.transform = 'none';
        });
      }
      return;
    }

    gsap.fromTo(
      titleRef.current?.children || [],
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: titleRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }, [isMobile]);

  // Live ventures first, then coming soon
  const sortedVentures = [...venturesData].sort((a, b) => {
    if (a.status === 'live' && b.status !== 'live') return -1;
    if (a.status !== 'live' && b.status === 'live') return 1;
    return 0;
  });

  return (
    <section
      ref={sectionRef}
      id="ventures"
      className="section relative overflow-hidden"
      style={{ background: '#0A0A0A' }}
      data-testid="ventures-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="grid-pattern opacity-30" />
      </div>

      <div className="container relative z-10">
        {/* Section Title */}
        <div ref={titleRef} className="text-center mb-16">
          <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
            Our Ventures
          </p>
          <h2 className="display-large text-white mb-6">
            BUILT FOR THE<br />
            <span className="text-stroke text-white">SPORTS INDUSTRY</span>
          </h2>
          <p className="body-large text-white/70 max-w-2xl mx-auto">
            Software, intelligence, and tools built at the intersection of sports and technology.
          </p>
        </div>

        {/* Ventures Grid — 3 live on top, 2 coming soon + custom solutions on bottom */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedVentures.map((venture, index) => (
            <VentureCard key={venture.id} venture={venture} index={index} />
          ))}
          <CustomSolutionsCard index={sortedVentures.length} />
        </div>
      </div>
    </section>
  );
};

export default VenturesSection;
